import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { useDictation } from '../lib/dictation.ts';
import { takeDroppedImages, useImageDrop } from '../lib/drop.ts';
import { Icon } from '../lib/icons.tsx';
import { api } from '../lib/api.ts';
import type { ChatImage } from '../lib/protocol.ts';
import { MAX_CHAT_IMAGES, prepareImages, type PreparedImage } from '../lib/images.ts';

const MAX_ROWS_PX = 132;

export type ComposerHandle = {
  addFiles: (files: File[]) => Promise<void>;
};

type ComposerProps = {
  sessionId?: string;
  running: boolean;
  queued: number;
  /** `store` saves a task prompt instead of starting a run. */
  mode?: 'send' | 'store';
  /** Photos already saved on the task, shown when editing. */
  savedImages?: ChatImage[];
  /** Vision is text-only; chat and tasks still attach photos. */
  allowImages?: boolean;
  /** Override the localStorage draft key. Defaults to the session's chat/task draft. */
  draftKey?: string;
  placeholder?: string;
  submitLabel?: string;
  stopLabel?: string;
  /** Pause (running) vs stop (queued). */
  busyKind?: 'running' | 'queued';
  onSend: (text: string, images: { mime: string; data: string; id?: string }[]) => void;
  onStop: () => void;
};

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    sessionId,
    running,
    queued,
    mode = 'send',
    savedImages = [],
    allowImages = true,
    draftKey: draftKeyProp,
    placeholder,
    submitLabel,
    stopLabel,
    busyKind = 'queued',
    onSend,
    onStop,
  },
  ref,
) {
  const store = mode === 'store';
  const draftKey =
    draftKeyProp ?? (store ? `draft-task:${sessionId ?? 'none'}` : `draft:${sessionId ?? 'none'}`);
  const [text, setText] = useState(() => localStorage.getItem(draftKey) ?? '');
  const [images, setImages] = useState<PreparedImage[]>([]);
  const imagesRef = useRef<PreparedImage[]>([]);
  imagesRef.current = images;
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Dictated phrases append rather than replace, so a prompt can be spoken,
  // repaired with the keyboard, and then continued out loud.
  const dictation = useDictation((phrase) => {
    setText((prev) => (prev === '' || /\s$/.test(prev) ? prev + phrase : `${prev} ${phrase}`));
  });

  // Android can discard a backgrounded tab at any moment; a half-typed prompt
  // surviving that is worth four lines of localStorage.
  useEffect(() => {
    if (text) localStorage.setItem(draftKey, text);
    else localStorage.removeItem(draftKey);
  }, [text, draftKey]);

  useEffect(() => {
    return () => {
      for (const img of imagesRef.current) {
        if (!img.existingId) URL.revokeObjectURL(img.previewUrl);
      }
    };
  }, []);

  const savedKey = savedImages.map((img) => img.id).join(',');

  useEffect(() => {
    if (!store || !allowImages || !sessionId) return;
    setImages((prev) => {
      const extras = prev.filter((img) => !img.existingId);
      return [
        ...savedImages.map((img) => ({
          key: img.id,
          mime: img.mime,
          data: '',
          previewUrl: api.imageUrl(sessionId, img.id),
          existingId: img.id,
        })),
        ...extras,
      ];
    });
  }, [store, allowImages, sessionId, savedKey]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.overflowY = 'hidden';
    const needed = el.scrollHeight;
    el.style.height = `${Math.min(MAX_ROWS_PX, needed)}px`;
    if (needed > MAX_ROWS_PX) el.style.overflowY = 'auto';
  }, [text]);

  const addFiles = useCallback(async (files: File[]): Promise<void> => {
    if (!allowImages || files.length === 0) return;
    setBusy(true);
    setNote(null);
    try {
      const { images: next, error } = await prepareImages(files, imagesRef.current.length);
      setImages((prev) => [...prev, ...next]);
      if (error) setNote(error);
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'could not add that image');
    } finally {
      setBusy(false);
    }
  }, [allowImages]);

  useImperativeHandle(ref, () => ({ addFiles }), [addFiles]);

  const tookDrop = useRef(false);
  useEffect(() => {
    if (!sessionId || !allowImages || tookDrop.current) return;
    const files = takeDroppedImages(sessionId, store ? 'task' : 'chat');
    if (files.length === 0) return;
    tookDrop.current = true;
    void addFiles(files);
  }, [sessionId, store, allowImages, addFiles]);

  const drop = useImageDrop(addFiles, allowImages);

  const removeImage = (key: string): void => {
    setImages((prev) => {
      const target = prev.find((img) => img.key === key);
      if (target && !target.existingId) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((img) => img.key !== key);
    });
  };

  const submit = (): void => {
    const value = text.trim();
    if (!value && images.length === 0) return;
    if (busy) return;
    dictation.cancel();
    setText('');
    const payload = images.map(({ mime, data, existingId }) =>
      existingId ? { mime, data: '', id: existingId } : { mime, data },
    );
    for (const img of images) {
      if (!img.existingId) URL.revokeObjectURL(img.previewUrl);
    }
    if (!store) setImages([]);
    setNote(null);
    onSend(value, payload);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Plain Enter must stay a newline: the on-screen keyboard has no other way
    // to make one, and prompts are usually multi-line.
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      submit();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
    if (!allowImages) return;
    const files = [...e.clipboardData.items]
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (files.length === 0) return;
    e.preventDefault();
    void addFiles(files);
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    void addFiles(files);
  };

  const canSend = (text.trim().length > 0 || images.length > 0) && !busy;

  return (
    <div className={`composer${drop.over ? ' is-drop-target' : ''}`} {...drop.handlers}>
      {queued > 0 && (
        <div className="composer-queue">
          {queued} message{queued > 1 ? 's' : ''} queued
        </div>
      )}
      {dictation.listening && (
        <div className="composer-interim">
          <span className="rec-dot" aria-hidden="true" />
          <span className="composer-interim-text">{dictation.interim || 'Listening...'}</span>
        </div>
      )}
      {(note || dictation.error) && (
        <div className="composer-note" aria-live="polite">
          {note || dictation.error}
        </div>
      )}
      {images.length > 0 && (
        <div className="composer-thumbs">
          {images.map((img) => (
            <div key={img.key} className="composer-thumb">
              <img src={img.previewUrl} alt="" />
              <button
                type="button"
                className="composer-thumb-x"
                onClick={() => removeImage(img.key)}
                aria-label="Remove photo"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="composer-row">
        {allowImages && (
          <>
            <button
              type="button"
              className="btn-attach"
              onClick={() => fileRef.current?.click()}
              disabled={images.length >= MAX_CHAT_IMAGES || busy}
              aria-label="Add photo"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <rect
                  x="3.5"
                  y="5.5"
                  width="17"
                  height="13"
                  rx="2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle cx="9" cy="10.5" r="1.6" fill="currentColor" />
                <path
                  d="M6.5 16.5l4.2-4.2 2.3 2.3 3-3.4 4 5.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={onPick}
            />
          </>
        )}
        {dictation.available && (
          <button
            type="button"
            className={dictation.listening ? 'btn-mic listening' : 'btn-mic'}
            onClick={dictation.toggle}
            aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
            aria-pressed={dictation.listening}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <rect x="9.5" y="3" width="5" height="9.5" rx="2.5" fill="currentColor" />
              <path
                d="M6.5 11.5a5.5 5.5 0 0011 0M12 17.2v3.3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        <textarea
          ref={textRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          rows={1}
          placeholder={placeholder}
          enterKeyHint="enter"
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
        />
        {running && !store ? (
          <button
            type="button"
            className={busyKind === 'running' ? 'btn-pause' : 'btn-stop'}
            onClick={onStop}
            aria-label={stopLabel ?? (busyKind === 'running' ? 'Pause the agent' : 'Stop the agent')}
          >
            <Icon name={busyKind === 'running' ? 'pause' : 'stop'} />
          </button>
        ) : (
          <button
            type="button"
            className="btn-send"
            onClick={submit}
            disabled={!canSend}
            aria-label={submitLabel ?? (store ? 'Save task' : 'Send')}
          >
            <Icon name="send" />
          </button>
        )}
      </div>
    </div>
  );
});
